import { db, type PosDatabase } from '@core/db/db'
import type {
  CorrectionInfo,
  FiscalReceiptData,
  OfflineQueueItem,
  ReceiptDocType,
  SettlementType
} from '@core/db/models'
import { fiscalService } from '@core/services/fiscal/FiscalService'
import { isNativeKkt } from '@core/native/kkt'
import { debugLog } from '@core/utils/log'

type FiscalQueueDb = Pick<PosDatabase, 'offlineQueue' | 'receipts' | 'sales_queue'>
type FiscalQueueFiscal = Pick<
  typeof fiscalService,
  'getKktStatus' | 'getMarkingReadiness' | 'printFiscalReceipt'
>

/** Полезная нагрузка элемента очереди дофискализации (type='fiscalize'). */
interface FiscalizePayload {
  receiptId: string
  total: number
  items: Array<{
    name: string
    price: number
    quantity: number
    unit?: string
    marking_code?: string
  }>
  /** Для чеков коррекции: тип документа, признак расчёта и реквизиты. */
  docType?: ReceiptDocType
  settlementType?: SettlementType
  correction?: CorrectionInfo
}

/** Разобрать payload очереди в FiscalizePayload (или null при невалидных данных). */
function parsePayload(raw: unknown): FiscalizePayload | null {
  if (!raw || typeof raw !== 'object') return null
  const p = raw as Partial<FiscalizePayload>
  if (!p.receiptId || typeof p.total !== 'number' || !Array.isArray(p.items)) return null
  return {
    receiptId: p.receiptId,
    total: p.total,
    items: p.items.map((i) => ({
      name: String(i?.name ?? 'Товар'),
      price: Number(i?.price) || 0,
      quantity: Number(i?.quantity) || 1,
      unit: i?.unit,
      marking_code: i?.marking_code
    })),
    docType: p.docType,
    settlementType: p.settlementType,
    correction: p.correction
  }
}

/**
 * Воркер дофискализации чеков (ФЗ-54).
 *
 * Обрабатывает СТРОГО чеки со статусом 'offline_pending' — через очередь
 * `offlineQueue` (type='fiscalize', status='pending'). Чеки 'non_fiscal'
 * (кассир умышленно отказался) не трогаются никогда.
 *
 * Триггеры: периодический таймер (по умолчанию 60 с), однократный запуск при
 * старте и колбэк FiscalService.onConnect (как только ККТ вернулась в строй).
 */
export class FiscalQueueWorker {
  private intervalId: number | null = null
  private isProcessing = false

  constructor(
    private readonly database: FiscalQueueDb = db,
    private readonly fiscal: FiscalQueueFiscal = fiscalService
  ) {}

  /**
   * Обработать отложенные фискальные чеки. Если ККТ не в состоянии 'ok'
   * (нет бумаги/смены/связи) — не делает ничего: очередь ждёт.
   * @returns количество успешно дофискализированных чеков.
   */
  public async processPending(): Promise<number> {
    // Гейт по состоянию ККТ, а не по «есть WS с агентом»: бумага/смена/ошибка
    // делают печать невозможной, попытки только копили бы ошибки.
    if (this.fiscal.getKktStatus()?.state !== 'ok') return 0
    if (this.isProcessing) return 0
    this.isProcessing = true

    let processed = 0
    try {
      const items = await this.database.offlineQueue.where('type').equals('fiscalize').toArray()
      const pending = items.filter((i: OfflineQueueItem) => i.status === 'pending')

      for (const item of pending) {
        if (item.id == null) continue
        if (await this.processItem(item.id)) processed += 1
      }
    } catch (err) {
      console.error('[FiscalQueueWorker] processPending failed:', err)
    } finally {
      this.isProcessing = false
    }

    if (processed > 0) {
      debugLog(`[FiscalQueueWorker] Дофискализировано чеков: ${processed}`)
    }
    return processed
  }

  /**
   * Точечно повторить один чек из очереди (кнопка «Повторить» в админке).
   * @returns true, если чек удалось дофискализировать.
   */
  public async processItem(id: number): Promise<boolean> {
    const item = await this.database.offlineQueue.get(id)
    if (!item || item.type !== 'fiscalize' || item.status !== 'pending') return false

    const payload = parsePayload(item.payload)
    if (!payload) {
      await this.database.offlineQueue.update(id, {
        status: 'failed',
        lastError: 'Некорректная нагрузка элемента очереди',
        lastAttemptAt: Date.now()
      })
      return false
    }

    const receipt = await this.database.receipts.where('receiptId').equals(payload.receiptId).first()
    if (receipt?.id == null) {
      await this.database.offlineQueue.update(id, {
        status: 'failed',
        lastError: 'Чек не найден в локальной базе',
        lastAttemptAt: Date.now()
      })
      return false
    }

    // Старые маркированные записи не должны бесконечно ретраиться, если
    // native KKT не прошёл preflight ФФД 1.2/ЕСМ/тег 2000.
    const markingReadiness = this.fiscal.getMarkingReadiness()
    if (
      isNativeKkt() &&
      payload.items.some((item) => Boolean(item.marking_code)) &&
      !markingReadiness.ok
    ) {
      await this.database.offlineQueue.update(id, {
        status: 'failed',
        lastError:
          `Маркировка ФФД 1.2 недоступна: ${markingReadiness.reason ?? 'ККТ не готова'}`,
        lastAttemptAt: Date.now()
      })
      return false
    }

    try {
      const result = await this.fiscal.printFiscalReceipt({
        id: payload.receiptId,
        // В ККТ сумма всегда положительная; возврат задаётся признаком расчёта.
        total_amount: Math.abs(payload.total),
        paymentMethod: receipt.paymentMethod,
        items: payload.items,
        // Коррекция: тип документа/признак расчёта/реквизиты из payload очереди.
        docType: payload.docType ?? receipt.docType,
        settlementType: payload.settlementType ?? receipt.settlementType,
        correction: payload.correction ?? receipt.correction
      })
      const fiscalData: FiscalReceiptData = {
        fiscalDocumentNumber: result.fiscalDocumentNumber,
        fiscalDocumentSign: result.fiscalDocumentSign,
        shiftNumber: result.shiftNumber,
        fiscalDriveNumber: result.fiscalDriveNumber,
        receiptNumber: result.receiptNumber,
        dateTime: result.dateTime
      }
      // Чек стал фискальным: обновляем статус и реквизиты в receipts.
      await this.database.receipts.update(receipt.id, { status: 'fiscalized', fiscalData })
      await this.database.offlineQueue.update(id, {
        status: 'sent',
        lastError: undefined,
        lastAttemptAt: Date.now()
      })
      // Помечаем чек к повторной отправке в облако, чтобы там обновился
      // статус (изначально он улетел как offline_pending).
      try {
        await this.database.sales_queue.update(payload.receiptId, { is_synced: false, shift_number: result.shiftNumber })
      } catch {
        /* чек уже мог быть вычищен из очереди синка — это не критично */
      }
      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      console.warn('[FiscalQueueWorker] дофискализация не удалась:', message)
      // Оставляем элемент в 'pending' для ретрая, фиксируем попытку и ошибку.
      await this.database.offlineQueue.update(id, {
        attempts: (item.attempts ?? 0) + 1,
        lastError: message,
        lastAttemptAt: Date.now()
      })
      return false
    }
  }

  /** Запустить периодическую обработку (и сразу один прогон). */
  public start(intervalMs = 60000): void {
    if (this.intervalId) return
    void this.processPending()
    this.intervalId = window.setInterval(() => {
      void this.processPending()
    }, intervalMs)
  }

  public stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  }
}

export const fiscalQueueWorker = new FiscalQueueWorker()